import React, { useState } from "react";
import { CogIcon, TrashIcon, PlusIcon } from "../../../assets/icons";
import ElementSelectorModal from "./ElementSelectorModal";
import Elements from "../side-toolbar/Elements";
import { useSelector, useDispatch } from 'react-redux';
import { fetchActiveStep, removeLayer, swapLayers, addLayerAfter, removeLayerConfig } from "../../../../../../redux/stepsSlice";
import { addLayerToActive } from "../../../../../../redux/stepsSlice";
import { useDrag, useDrop } from 'react-dnd';
import { generateRandomId } from "../../../../../pages/utils/tools";
import { getActiveStep, selectActiveLayer, setActiveLayer } from "../../../../../../redux/toolsSlice";
import { LargeTextLayerContentDefaults } from "./fields/LargeTextContent";
import { videoLayerDefaults } from "./fields/VideoContent";
import { ButtonLayerDefaults } from "./fields/ButtonContent";
import { BodyTextLayerContentDefaults } from "./fields/BodyTextContent";
import { EmbedCodeLayerDefaults } from "./fields/EmbedCodeContent";
import { ImageLayerDefaults } from "./fields/ImageContent";
import { AttachmentLayerDefaults } from "./fields/AttachmentContent.jsx";
import SelectedWrapper from "./SelectedWrapper.jsx";
import { ShortAnswerLayerDefaults } from "./fields/ShortAnswerContent.jsx";
import { LongAnswerLayerDefaults } from "./fields/LongAnswerContent.jsx";
import { CheckboxLayerDefaults } from "./fields/CheckboxContent.jsx";
import { MultiSelectLayerDefaults } from "./fields/MultiSelectContent.jsx";
import { FileUploadLayerDefaults } from "./fields/FileUploadContent.jsx";

const ItemTypes = {
  ELEMENT: 'element',
};

const ElementWrapper = ({ children, layer }) => {

  const [isVisible, setIsVisible] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);

  const dispatch = useDispatch();

  const activeStep = useSelector((state) => getActiveStep(state));
  const activeLayer = useSelector((state) => selectActiveLayer(state));
  const stepId = activeStep.id;
  const isSelected = activeLayer && activeLayer.id == layer.id;

  const handleAddClick = (layer) => {
    dispatch(setActiveLayer({}));
    dispatch(removeLayerConfig());
    setIsVisible(!isVisible);
  }

  const getLayerDefault = (type) => {
    switch (type) {
      case 'large_text':
        return LargeTextLayerContentDefaults(generateRandomId());
      case 'video':
        return videoLayerDefaults(generateRandomId());
      case 'button':
        return ButtonLayerDefaults(generateRandomId());
      case 'body':
        return BodyTextLayerContentDefaults(generateRandomId());
      case 'embedded_code':
        return EmbedCodeLayerDefaults(generateRandomId());
      case 'image':
        return ImageLayerDefaults(generateRandomId());
      case 'attachment':
        return AttachmentLayerDefaults(generateRandomId());
      case 'short_answer':
        return ShortAnswerLayerDefaults(generateRandomId());
      case 'long_answer':
        return LongAnswerLayerDefaults(generateRandomId());
      case 'checkbox':
        return CheckboxLayerDefaults(generateRandomId());
      case 'multi_select':
        return MultiSelectLayerDefaults(generateRandomId());
      case 'file_upload':
        return FileUploadLayerDefaults(generateRandomId());
      default:
        return {
          id: generateRandomId(),
          type: selectedElement,
          content: 'Hello world over here',
        }
    }
  }

  const handleInsertElement = () => {
    if (!selectedElement) return;
    dispatch(fetchActiveStep()).then(() => {
      const newLayer = getLayerDefault(selectedElement);
      if (layer.id) {
        dispatch(addLayerAfter({ stepId, afterLayerId: layer.id, newLayer }));
      } else {
        dispatch(addLayerToActive({ layer: newLayer }));
      }
      setIsVisible(false);
    });
  };

  // To delete the selected element
  const handleDeleteClick = () => {
    if (confirm('Are you sure you want to delete the element?')) {
      dispatch(removeLayer({ stepId: activeStep.id, layerId: layer.id }));
      dispatch(setActiveLayer({}));
      dispatch(removeLayerConfig());
    }
  }

  // Duplicate this element directly beneath itself (deep clone + fresh id).
  const handleDuplicateClick = () => {
    dispatch(fetchActiveStep()).then(() => {
      const clone = JSON.parse(JSON.stringify(layer));
      clone.id = generateRandomId();
      dispatch(addLayerAfter({ stepId, afterLayerId: layer.id, newLayer: clone }));
    });
  }

  const handleClose = () => setIsVisible(false);

  // Drag source
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ELEMENT,
    item: { id: layer.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Drop target
  const [, drop] = useDrop({
    accept: ItemTypes.ELEMENT,
    drop: (item) => {
      dispatch(swapLayers({ stepId: activeStep.id, layerId1: item.id, layerId2: layer.id }));
    },
  });

  const removeFocusElement = (e) => {
    if (e.target === e.currentTarget) {
      dispatch(setActiveLayer({}));
      dispatch(removeLayerConfig());
    }
  }

  return (
    <div
      role="element-wrapper"
      className={`un-path-element-row un-path-element-row--${layer.type || 'element'} ${isSelected ? 'is-selected' : ''}`}
      ref={(node) => drag(drop(node))}
      onClick={removeFocusElement}
    >
      <div className="un-path-element-row__controls">
        <button type="button" className="un-path-element-row__control-button is-danger" aria-label="Delete element" title="Delete element" onClick={() => handleDeleteClick()}>
          <TrashIcon />
          <span>Delete</span>
        </button>
        <button type="button" className="un-path-element-row__control-button" aria-label="Duplicate element" title="Duplicate element" onClick={handleDuplicateClick}>
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><rect x="7" y="7" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M4 13V5a1 1 0 0 1 1-1h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          <span>Duplicate</span>
        </button>
        <button type="button" className="un-path-element-row__control-button" aria-label="Add element after this one" title="Add element after this one" onClick={() => handleAddClick(layer)}>
          <PlusIcon />
          <span>Add</span>
        </button>
        <button type="button" className="un-path-element-row__control-button un-path-element-row__control-button--icon-only mr-2" aria-label="Element settings" title="Element settings">
          <CogIcon />
        </button>
      </div>
      <div className="un-path-element-row__content">
        <SelectedWrapper visibility={isSelected}>
          {children}
        </SelectedWrapper>
      </div>
      <button
        type="button"
        className="un-pe-addbetween"
        aria-label="Add an element here"
        onClick={() => handleAddClick(layer)}
      >
        <span className="un-pe-addbetween__btn">&#65291; Add element</span>
      </button>
      {
        isVisible &&
        <ElementSelectorModal
          title="Add an element"
          show={isVisible}
          onClose={handleClose}
          children={<Elements closeModal={handleClose} afterLayerId={layer.id} setSelectedElement={setSelectedElement} />}
        />
      }
    </div>
  );
}

export default ElementWrapper;
